import { useState, useEffect } from 'react'
import { X, Share, Plus } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIOSGuide, setShowIOSGuide] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
    || ('standalone' in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true)

  useEffect(() => {
    if (sessionStorage.getItem('pwa-prompt-dismissed')) {
      setDismissed(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  // iOS: show guide after a few seconds if not standalone
  useEffect(() => {
    if (isIOS && !isInStandaloneMode && !dismissed && !sessionStorage.getItem('pwa-prompt-dismissed')) {
      const t = setTimeout(() => setShowIOSGuide(true), 3000)
      return () => clearTimeout(t)
    }
  }, [isIOS, isInStandaloneMode, dismissed])

  const handleDismiss = () => {
    sessionStorage.setItem('pwa-prompt-dismissed', '1')
    setDismissed(true)
    setShowIOSGuide(false)
    setDeferredPrompt(null)
  }

  const handleInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setDeferredPrompt(null)
      setDismissed(true)
    }
  }

  if (isInStandaloneMode || dismissed) return null

  // Android: install button
  if (deferredPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 flex items-center justify-between gap-3 rounded-xl bg-blue-600 px-4 py-3 shadow-xl text-white">
        <div className="flex items-center gap-3 min-w-0">
          <Plus className="w-5 h-5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">安裝 App</p>
            <p className="text-xs opacity-80 truncate">加到桌面，快速開啟</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleInstall}
            className="rounded-lg bg-white text-blue-600 px-3 py-1.5 text-sm font-bold hover:bg-blue-50 transition-colors"
          >
            安裝
          </button>
          <button onClick={handleDismiss} className="p-1 opacity-70 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  // iOS: guide
  if (showIOSGuide) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 rounded-xl bg-gray-900 px-4 py-4 shadow-xl text-white">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-sm font-semibold">安裝 App 到桌面</p>
          <button onClick={handleDismiss} className="p-1 opacity-70 hover:opacity-100 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-300">
          <div className="flex items-center gap-1.5">
            <span className="text-xs bg-gray-700 rounded px-1.5 py-0.5">1</span>
            <span>點底部</span>
            <Share className="w-4 h-4 text-blue-400 inline" />
            <span>分享</span>
          </div>
          <span className="text-gray-600">→</span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs bg-gray-700 rounded px-1.5 py-0.5">2</span>
            <span>「加入主畫面」</span>
          </div>
        </div>
        {/* Arrow pointing down for iOS Safari bottom bar */}
        <div className="mt-2 flex justify-center">
          <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-900" />
        </div>
      </div>
    )
  }

  return null
}
